import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { LanguageProvider } from './contexts/LanguageContext';
import { AdminAuthProvider } from './contexts/AdminAuthContext';
import { InfluencerAuthProvider } from './contexts/InfluencerAuthContext';
import { useMetaTags } from './hooks/useMetaTags';
import ScrollToTop from './components/ScrollToTop';

// Public Pages
import IndexPage from './pages/IndexPage';
import AboutPage from './pages/AboutPage';
import SupportPage from './pages/SupportPage';
import PolicyPage from './pages/PolicyPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsPage from './pages/TermsPage';
import PartnershipsPage from './pages/PartnershipsPage';
import PricingPage from './pages/PricingPage';
import NutritionPage from './pages/NutritionPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import DownloadPage from './pages/DownloadPage';
import UnsubscribePage from './pages/UnsubscribePage';
import NotFoundPage from './pages/NotFoundPage';

// Admin Pages
import AdminLoginPage from './pages/admin/AdminLoginPage';
import DashboardPage from './pages/admin/DashboardPage';
import InfluencersPage from './pages/admin/InfluencersPage';
import CouponsPage from './pages/admin/CouponsPage';
import UsersPage from './pages/admin/UsersPage';
import CommissionsPage from './pages/admin/CommissionsPage';
import AdminSupportPage from './pages/admin/SupportPage';
import UsersTempPage from './pages/admin/UsersTempPage';
import AdminsPage from './pages/admin/AdminsPage';
import MessageLogsPage from './pages/admin/MessageLogsPage';
import NotificationsPage from './pages/admin/NotificationsPage';
import SurveysPage from './pages/admin/SurveysPage';
import IngredientsPage from './pages/admin/IngredientsPage';
import MealsPage from './pages/admin/MealsPage';
import UserMealsPage from './pages/admin/UserMealsPage';
import ActivitiesPage from './pages/admin/ActivitiesPage';
import AppVersionsPage from './pages/admin/AppVersionsPage';

// Admin Components
import ProtectedRoute from './components/admin/ProtectedRoute';
import AdminLayout from './components/admin/AdminLayout';

// Influencer Pages
import InfluencerLoginPage from './pages/influencer/InfluencerLoginPage';
import InfluencerVerifyPage from './pages/influencer/InfluencerVerifyPage';
import InfluencerDashboardPage from './pages/influencer/InfluencerDashboardPage';
import InfluencerProfilePage from './pages/influencer/InfluencerProfilePage';

// Influencer Components
import InfluencerProtectedRoute from './components/influencer/InfluencerProtectedRoute';
import InfluencerLayout from './components/influencer/InfluencerLayout';

import './styles/global.css';
import './components/ui/ui.css';

// Componente interno que usa o hook
const AppContent = () => {
  useMetaTags(); // Aplicar meta tags dinâmicas
  
  return (
    <BrowserRouter>
      <ScrollToTop />
      <Routes>
        {/* ==================== PUBLIC ROUTES ==================== */}
        <Route path="/" element={<IndexPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/support" element={<SupportPage />} />
        <Route path="/plans" element={<PricingPage />} />
        <Route path="/nutrition" element={<NutritionPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/policy" element={<Navigate to="/privacy" replace />} />
        <Route path="/partnerships" element={<PartnershipsPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/download" element={<DownloadPage />} />
        <Route path="/unsubscribe" element={<UnsubscribePage />} />

        {/* ==================== ADMIN ROUTES ==================== */}
        
        {/* Login (public) */}
        <Route path="/admin/login" element={<AdminLoginPage />} />

        {/* Protected Admin Routes */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <DashboardPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/influencers"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <InfluencersPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/coupons"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <CouponsPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/users"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <UsersPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/commissions"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <CommissionsPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/support"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <AdminSupportPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/users-temp"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <UsersTempPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/admins"
          element={
            <ProtectedRoute requiredRole="super_admin">
              <AdminLayout>
                <AdminsPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/message-logs"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <MessageLogsPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/notifications"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <NotificationsPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/surveys"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <SurveysPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/ingredients"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <IngredientsPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/meals"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <MealsPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        {/* Redirect antigo meals-stats para meals */}
        <Route path="/admin/meals-stats" element={<Navigate to="/admin/meals" replace />} />

        <Route
          path="/admin/users/:id/meals"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <UserMealsPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/activities"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <ActivitiesPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/app-versions"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <AppVersionsPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        {/* Redirect /admin/settings para /admin/app-versions */}
        <Route path="/admin/settings" element={<Navigate to="/admin/app-versions" replace />} />

        {/* ==================== INFLUENCER ROUTES ==================== */}
        
        {/* Login (public) */}
        <Route path="/influencer/login" element={<InfluencerLoginPage />} />
        <Route path="/influencer/verify" element={<InfluencerVerifyPage />} />

        {/* Protected Influencer Routes */}
        <Route
          path="/influencer"
          element={
            <InfluencerProtectedRoute>
              <InfluencerLayout>
                <InfluencerDashboardPage />
              </InfluencerLayout>
            </InfluencerProtectedRoute>
          }
        />

        <Route
          path="/influencer/profile"
          element={
            <InfluencerProtectedRoute>
              <InfluencerLayout>
                <InfluencerProfilePage />
              </InfluencerLayout>
            </InfluencerProtectedRoute>
          }
        />

        {/* ==================== 404 ==================== */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </BrowserRouter>
  );
};

function App() {
  return (
    <LanguageProvider>
      <AdminAuthProvider>
        <InfluencerAuthProvider>
          <AppContent />
        </InfluencerAuthProvider>
      </AdminAuthProvider>
    </LanguageProvider>
  );
}

export default App;
